import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { SecureImage } from "@/components/SecureImage";
import { formatINR, formatDateTime } from "@/lib/format";
import { Loader2, CheckCircle2, XCircle, Info, Eye } from "lucide-react";
import { toast } from "sonner";

export default function AdminRepayments() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = async (st = status) => {
    const { data } = await api.get("/admin/repayments", { params: { status: st } });
    setSubs(data);
    setLoading(false);
  };
  useEffect(() => { load(status); }, [status]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setNote(""); await load(status); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Repayment Verification</h1>
          <p className="text-muted-foreground text-sm">Review and verify customer repayment submissions.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="repayment-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="info_requested">Info Requested</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {subs.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground ip-card-shadow">No repayment submissions.</Card>
      ) : (
        <div className="space-y-4">
          {subs.map((s) => (
            <Card key={s.id} className="p-5 ip-card-shadow" data-testid={`repayment-${s.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="font-semibold">{s.customer_name} <span className="font-mono text-muted-foreground font-normal">· {s.reference_number}</span></p>
                  <p className="text-xs text-muted-foreground">Submitted {formatDateTime(s.created_at)}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div className="grid sm:grid-cols-4 gap-4 mt-4 text-sm">
                <div><p className="text-xs text-muted-foreground">Amount</p><p className="font-mono font-semibold">{formatINR(s.amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">UTR</p><p className="font-mono">{s.utr}</p></div>
                <div><p className="text-xs text-muted-foreground">Payment date</p><p>{s.payment_date}</p></div>
                <div><p className="text-xs text-muted-foreground">Current outstanding</p><p className="font-mono font-semibold">{formatINR(s.outstanding)}</p></div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid={`repayment-view-${s.id}`}><Eye className="h-4 w-4 mr-1.5" /> View Screenshot</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Payment screenshot — {s.reference_number}</DialogTitle></DialogHeader>
                    <SecureImage fileId={s.screenshot_file_id} className="w-full max-h-[70vh] object-contain rounded-lg border border-border" />
                  </DialogContent>
                </Dialog>

                {s.status === "pending" && (
                  <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(() => api.post(`/admin/repayments/${s.id}/confirm`), "Payment confirmed")} data-testid={`repayment-confirm-${s.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm Payment
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild><Button size="sm" variant="outline" className="text-rose-600 border-rose-200">Reject</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Reject payment</DialogTitle></DialogHeader>
                        <Textarea placeholder="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                        <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={() => act(() => api.post(`/admin/repayments/${s.id}/reject`, { note }), "Payment rejected")}><XCircle className="h-4 w-4 mr-1.5" /> Confirm Reject</Button>
                      </DialogContent>
                    </Dialog>
                    <Dialog>
                      <DialogTrigger asChild><Button size="sm" variant="outline"><Info className="h-4 w-4 mr-1.5" /> Request Info</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Request more information</DialogTitle></DialogHeader>
                        <Textarea placeholder="What do you need?" value={note} onChange={(e) => setNote(e.target.value)} />
                        <Button disabled={busy} onClick={() => act(() => api.post(`/admin/repayments/${s.id}/request-info`, { note }), "Information requested")}>Send</Button>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                {s.note && <span className="text-xs text-muted-foreground">Note: {s.note}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
